/* Kennedy's Karne theme scripts: header, menu, cart drawer, product form, hero video. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const routes = window.theme?.routes || {};
  const strings = window.theme?.strings || {};

  /* ---------- Header shadow on scroll ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile menu ---------- */
  const menu = $('#MobileMenu');
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-menu-open]')) menu?.showModal();
    if (e.target.closest('[data-menu-close]')) menu?.close();
  });

  /* ---------- Cart drawer ---------- */
  const drawer = $('[data-cart-drawer]');
  const openDrawer = () => {
    if (!drawer || drawer.open) return;
    drawer.showModal();
  };
  const closeDrawer = () => drawer?.close();

  drawer?.addEventListener('click', (e) => {
    if (e.target === drawer) closeDrawer(); // backdrop click
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && drawer && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      openDrawer();
    }
  });

  const renderCart = (sections) => {
    const html = sections?.['cart-drawer'];
    if (!html || !drawer) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-region]', doc);
    const region = $('[data-cart-region]', drawer);
    if (fresh && region) region.replaceWith(fresh);
    const count = $('[data-cart-count-source]', drawer)?.textContent.trim() || '0';
    $$('[data-cart-count]').forEach((el) => (el.textContent = count === '0' ? '' : count));
  };

  const cartRequest = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ['cart-drawer'], sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
    return data;
  };

  /* Add to cart: product page form and quick-add buttons */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form], [data-add-form]');
    if (!form || !drawer) return;
    e.preventDefault();
    const button = $('[type="submit"]', form);
    const errorEl = $('[data-form-error]', form);
    const fd = new FormData(form);
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;
    try {
      const data = await cartRequest(`${routes.cartAdd}.js`, {
        items: [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }],
      });
      renderCart(data.sections);
      $$('[data-cart-count]').forEach((el) => {
        el.classList.remove('is-bumped');
        void el.offsetWidth; // restart the animation
        el.classList.add('is-bumped');
      });
      if (button?.classList.contains('card__add')) {
        // Quick add from a product grid: confirm in place so shoppers can keep browsing.
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1800);
      } else {
        openDrawer();
      }
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message || strings.error;
        errorEl.hidden = false;
      }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  });

  /* Quantity changes and removals inside the cart */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    const onCartPage = document.body.classList.contains('template-cart');
    const scope = btn.closest('[data-cart-region], [data-cart-page]');
    scope?.setAttribute('data-cart-busy', 'true');
    try {
      const data = await cartRequest(`${routes.cartChange}.js`, {
        id: btn.dataset.key,
        quantity: Math.max(0, Number(btn.dataset.quantity)),
      });
      if (onCartPage) {
        window.location.reload();
        return;
      }
      renderCart(data.sections);
    } catch (err) {
      scope?.removeAttribute('data-cart-busy');
      window.alert(err.message || strings.error);
    }
  });

  /* ---------- Product page ---------- */
  $$('[data-product-section]').forEach((section) => {
    const form = $('[data-product-form]', section);
    const qty = $('input[name="quantity"]', section);

    // Gallery thumbnails
    const stage = $('.stage-main', section);

    /* Quantity shelf: show up to 5 items; past 5, keep the shelf full and show a "×N" badge.
       Tall items (bottles) fan out side by side; wide items (tubs) stack two rows high. */
    const stageBox = $('[data-stage]', section);
    const countBadge = $('[data-stage-count]', section);
    const MAX_ON_SHELF = 5;
    // Each layout lists every item including the main one: [x, y, scale, z]
    let shape = { wide: false, cutout: false, w: 0.4, h: 0.75, cx: 0.5, cy: 0.5, aspect: 0.75 };

    // Where the item actually sits inside the stage, as shares of the stage size.
    const geometry = () => {
      const box = stageBox.getBoundingClientRect();
      const pad = 0.08 * box.width; // matches the stage image padding
      const innerW = box.width - pad * 2, innerH = box.height - pad * 2;
      const rw = Math.min(innerW, innerH * shape.aspect), rh = rw / shape.aspect;
      const left = (box.width - rw) / 2, top = (box.height - rh) / 2;
      return {
        cw: (shape.w * rw) / box.width,
        ch: (shape.h * rh) / box.height,
        cx: (left + shape.cx * rw) / box.width,
        cy: (top + shape.cy * rh) / box.height,
      };
    };
    // Soft oval mask fitted to the item, so a photo's background never shows as a box.
    const applyMask = () => {
      if (!stageBox) return;
      const g = geometry();
      stageBox.style.setProperty('--mask-x', `${(g.cx * 100).toFixed(1)}%`);
      stageBox.style.setProperty('--mask-y', `${(g.cy * 100).toFixed(1)}%`);
      stageBox.style.setProperty('--mask-rx', `${(g.cw * 78).toFixed(1)}%`);
      stageBox.style.setProperty('--mask-ry', `${(g.ch * 70).toFixed(1)}%`);
    };

    // Tall items fan out: one in front, the rest stepping back to each side.
    // Spacing comes from the measured photo so every product overlaps by the same amount.
    const FAN_SLOTS = [[0, 1, 5, 0], [1, 0.9, 4, -1.5], [-1, 0.9, 4, -1.5], [2, 0.8, 3, -3], [-2, 0.8, 3, -3]];
    const fanLayout = (n) => {
      if (n === 1) return [[0, 0, 1, 5]];
      const { cw } = geometry();
      const OVERLAP = 0.92; // neighbours tuck ~8% behind each other
      const slots = FAN_SLOTS.slice(0, n);
      const place = (f) => slots.map(([side, rel, z, y]) => {
        const steps = Math.abs(side);
        let x = 0;
        for (let k = 1; k <= steps; k++) {
          const a = FAN_SLOTS[(k - 1) * 2][1], b = FAN_SLOTS[k * 2 - 1][1];
          x += (cw * f * (a + b) / 2) * OVERLAP;
        }
        return [Math.sign(side) * x, y, rel * f, z];
      });
      const extent = (items) => Math.max(...items.map(([x, , sc]) => Math.abs(x) + (cw * sc) / 2));
      const MAX_SIZE = { 2: 0.92, 3: 0.86, 4: 0.82, 5: 0.8 }; // keep a little air around small groups
      const f = Math.min(MAX_SIZE[n], 0.43 / extent(place(1)));
      return place(f).map(([x, y, sc, z]) => [x * 100, y, sc, z]);
    };

    // Wide items stack like a store display: 2 side by side, then 2+1, 3+1, 3+2.
    // Sizes come from the measured photo so items sit snugly whatever the product.
    const ROWS = { 2: [2], 3: [2, 1], 4: [3, 1], 5: [3, 2] };
    const stackLayout = (n) => {
      if (n === 1) return [[0, 0, 1, 5]];
      const { cw, ch } = geometry();
      const rows = ROWS[n];
      const cols = Math.max(...rows);
      const scale = Math.min(1, 0.86 / (cols * cw * 0.97), 0.9 / (rows.length * ch * 0.9));
      const step = cw * scale * 0.97;          // slight overlap between neighbours
      const rowH = ch * scale * 0.86;          // upper row rests a little into the lower one
      const out = [];
      rows.forEach((count, r) => {
        const y = rows.length === 1 ? 0 : (r === 0 ? rowH / 2 : -rowH / 2);
        for (let i = 0; i < count; i++) {
          const x = (i - (count - 1) / 2) * step;
          out.push([x * 100, y * 100, scale, r === 0 ? (i === 0 ? 5 : 4) : 6]);
        }
      });
      return out;
    };

    // Look at the photo once: is the item wider than tall, and is the background already transparent?
    const measure = async () => {
      if (!stage) return;
      try {
        const blob = await (await fetch(stage.currentSrc || stage.src)).blob();
        const bmp = await createImageBitmap(blob);
        const canvas = new OffscreenCanvas(bmp.width, bmp.height);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bmp, 0, 0);
        const { data } = ctx.getImageData(0, 0, bmp.width, bmp.height);
        const px = (x, y) => (y * bmp.width + x) * 4;
        const corner = px(2, 2);
        const cutout = data[corner + 3] < 20 && data[px(bmp.width - 3, bmp.height - 3) + 3] < 20;
        const bg = [data[corner], data[corner + 1], data[corner + 2]];
        let minX = bmp.width, maxX = 0, minY = bmp.height, maxY = 0;
        const step = Math.max(2, Math.round(bmp.width / 300));
        for (let y = 0; y < bmp.height; y += step) {
          for (let x = 0; x < bmp.width; x += step) {
            const i = px(x, y);
            // Cut-outs: any opaque pixel. Photos: pixels clearly different from the background
            // (a soft grey haze around the bottle is ignored).
            const solid = cutout
              ? data[i + 3] > 40
              : data[i + 3] > 40 && Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) > 150;
            if (solid) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
          }
        }
        if (maxX <= minX || maxY <= minY) throw new Error('nothing found');
        const w = (maxX - minX) / bmp.width, h = (maxY - minY) / bmp.height;
        shape = {
          wide: w > h * 1.15, cutout, w, h,
          cx: (minX + maxX) / 2 / bmp.width, cy: (minY + maxY) / 2 / bmp.height,
          aspect: bmp.width / bmp.height,
        };
      } catch (e) {
        shape = { wide: false, cutout: false, w: 0.4, h: 0.75, cx: 0.5, cy: 0.5, aspect: 0.75 };
      }
      applyMask();
      stageBox?.classList.toggle('is-cutout', shape.cutout);
      updateShelf(Math.max(1, Number(qty?.value) || 1));
    };

    const updateShelf = (n) => {
      if (!stageBox || !stage) return;
      const useCrowd = stageBox.dataset.crowd === 'true';
      const onShelf = useCrowd ? Math.min(Math.max(n, 1), MAX_ON_SHELF) : 1;
      const layout = shape.wide ? stackLayout(onShelf) : fanLayout(onShelf);
      stageBox.classList.toggle('is-crowded', onShelf > 1);
      stageBox.classList.toggle('is-stacked', shape.wide && onShelf > 1);

      const [mx, my, ms] = layout[0];
      stage.style.setProperty('--mx', `${mx}%`);
      stage.style.setProperty('--my', `${my}%`);
      stage.style.setProperty('--ms', ms);

      const current = $$('.crowd-bottle:not(.is-leaving)', stageBox);
      const wanted = onShelf - 1;
      for (let i = current.length; i < wanted; i++) {
        const img = document.createElement('img');
        img.className = 'crowd-bottle';
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.src = stage.currentSrc || stage.src;
        stageBox.insertBefore(img, stage);
        current.push(img);
      }
      current.slice(wanted).reverse().forEach((img) => {
        img.classList.add('is-leaving');
        img.addEventListener('animationend', () => img.remove(), { once: true });
        setTimeout(() => img.remove(), 400);
      });
      // Position every remaining extra item for the current layout (they glide when the layout changes)
      current.slice(0, wanted).forEach((img, i) => {
        const [x, y, sc, z] = layout[i + 1];
        img.style.setProperty('--x', `${x}%`);
        img.style.setProperty('--y', `${y}%`);
        img.style.setProperty('--s', sc);
        img.style.setProperty('--z', z);
        img.style.setProperty('--b', z >= 4 ? 0.95 : 0.88);
      });

      if (countBadge) {
        const showBadge = useCrowd ? n > MAX_ON_SHELF : n > 1;
        const text = `×${n}`;
        if (showBadge && countBadge.textContent !== text) {
          countBadge.textContent = text;
          countBadge.classList.remove('is-bumped');
          void countBadge.offsetWidth;
          countBadge.classList.add('is-bumped');
        }
        countBadge.hidden = !showBadge;
      }
    };
    if (stageBox?.dataset.crowd === 'true') {
      measure();
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => { applyMask(); updateShelf(Math.max(1, Number(qty?.value) || 1)); }, 150);
      });
    }
    const syncShelfSrc = () => $$('.crowd-bottle', stageBox || section).forEach((img) => (img.src = stage.currentSrc || stage.src));

    $$('[data-qty-step]', section).forEach((btn) =>
      btn.addEventListener('click', () => {
        const next = Math.max(1, (Number(qty.value) || 1) + Number(btn.dataset.qtyStep));
        qty.value = next;
        updateShelf(next);
      })
    );
    qty?.addEventListener('input', () => updateShelf(Math.max(1, Math.min(99, Number(qty.value) || 1))));
    stage?.addEventListener('load', () => { syncShelfSrc(); if (stageBox?.dataset.crowd === 'true') measure(); });
    $$('[data-thumb]', section).forEach((thumb) =>
      thumb.addEventListener('click', () => {
        if (!stage) return;
        stage.src = thumb.dataset.src;
        stage.srcset = thumb.dataset.srcset;
        stage.alt = thumb.dataset.alt;
        $$('[data-thumb]', section).forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
      })
    );

    // Variant selection
    const variantsEl = $('[data-variants]', section);
    if (!variantsEl || !form) return;
    const variants = JSON.parse(variantsEl.textContent);
    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add-button]', form);
    const priceWrap = $('[data-price-wrap]', section);
    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-input]')) return;
      const selected = $$('[data-option-input]:checked', form).map((i) => i.value);
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      if (!variant) {
        addBtn.disabled = true;
        addBtn.textContent = window.theme.strings.soldOut;
        return;
      }
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? addBtn.dataset.addLabel || 'Add to cart' : strings.soldOut;
      if (priceWrap) {
        const sale = variant.compare_at_price > variant.price;
        priceWrap.innerHTML = `<span class="price price--large"><span>${money(variant.price)}</span>${
          sale ? `<s>${money(variant.compare_at_price)}</s>` : ''
        }</span>`;
      }
      if (variant.featured_image && stage) {
        stage.src = variant.featured_image.src;
        stage.removeAttribute('srcset');
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    });
    if (addBtn) addBtn.dataset.addLabel = addBtn.textContent.trim();
  });

  /* ---------- Collection sort ---------- */
  $$('[data-sort-select]').forEach((select) =>
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    })
  );

  /* ---------- Hero video: pause control + reduced motion ---------- */
  $$('[data-hero]').forEach((hero) => {
    const toggle = $('[data-hero-toggle]', hero);
    const video = $('video', hero);
    const iframe = $('[data-hero-iframe]', hero);
    let playing = true;

    const send = (cmd) => {
      if (!iframe) return;
      if (iframe.dataset.heroIframe === 'vimeo') {
        iframe.contentWindow?.postMessage({ method: cmd }, '*');
      } else {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: cmd === 'pause' ? 'pauseVideo' : 'playVideo', args: [] }),
          '*'
        );
      }
    };
    const setPlaying = (state) => {
      playing = state;
      if (video) state ? video.play() : video.pause();
      send(state ? 'play' : 'pause');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(!state));
        $('[data-icon-pause]', toggle).hidden = !state;
        $('[data-icon-play]', toggle).hidden = state;
        $('.visually-hidden', toggle).textContent = state ? 'Pause background video' : 'Play background video';
      }
    };

    // Fade the video in over the still frame only once frames are actually playing.
    const reveal = () => hero.querySelectorAll('.hero__video').forEach((el) => el.classList.add('is-playing'));
    if (video) {
      if (video.readyState >= 3 && !video.paused) reveal();
      video.addEventListener('playing', reveal, { once: true });
      const tryPlay = video.play?.();
      if (tryPlay && tryPlay.catch) tryPlay.catch(() => {}); // autoplay blocked: the still frame stays
    } else if (iframe) {
      iframe.addEventListener('load', () => setTimeout(reveal, 900), { once: true });
    }

    // Keep the loop running whenever it's on screen (some browsers pause muted video on load,
    // in low-power mode or in background tabs), and pause it when scrolled away to save battery.
    if (video) {
      let userPaused = false;
      let onScreen = true;
      const resume = () => {
        if (userPaused || !onScreen || document.hidden || !playing) return;
        if (video.paused) video.play().catch(() => {});
      };
      video.addEventListener('canplay', resume);
      video.addEventListener('pause', () => setTimeout(resume, 250));
      document.addEventListener('visibilitychange', resume);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          onScreen = entry.isIntersecting;
          if (onScreen) resume();
          else video.pause();
        }).observe(hero);
      }
      toggle?.addEventListener('click', () => { userPaused = playing; }); // runs before the toggle flips 'playing'
    }

    toggle?.addEventListener('click', () => setPlaying(!playing));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (video) video.removeAttribute('autoplay');
      setTimeout(() => setPlaying(false), 600);
    }
  });
})();

/* Desktop menu dropdowns: open on hover, close on outside click or Escape. */
(() => {
  const dropdowns = [...document.querySelectorAll('[data-nav-dropdown]')];
  const canHover = window.matchMedia('(hover: hover)').matches;
  dropdowns.forEach((d) => {
    if (canHover) {
      d.addEventListener('mouseenter', () => (d.open = true));
      d.addEventListener('mouseleave', () => (d.open = false));
    }
  });
  document.addEventListener('click', (e) => {
    dropdowns.forEach((d) => { if (!d.contains(e.target)) d.open = false; });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    dropdowns.forEach((d) => { if (d.open) { d.open = false; d.querySelector('summary')?.focus(); } });
  });
})();

/* Scroll reveal for product and recipe grids. */
(() => {
  const groups = document.querySelectorAll('[data-reveal]');
  if (!groups.length) return;
  groups.forEach((g) => [...g.children].forEach((child, i) => child.style.setProperty('--i', i % 8)));
  if (!('IntersectionObserver' in window)) {
    groups.forEach((g) => g.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px' });
  groups.forEach((g) => io.observe(g));
})();
